import React from 'react';
import { FieldWrapper, Icon, InputField, InputWrapper, Label, LabelIcon } from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import MultiSelectInputComponent from './multiSelectInput';
import { InputProps } from '../../interfaces/inputInterface';
import { ICON_SIZE } from '../../global/constants';

/**
 * Input component props
 * @typedef {Object} InputProps
 * @property {'normal' | 'tags'} [inputType] - Type of input - either normal or tags input
 * @property {string} [label] - Label text displayed above the input
 * @property {string} [placeHolder] - Placeholder text displayed when input is empty
 * @property {string} value - Current value of the input
 * @property {(e: React.MouseEvent<HTMLDivElement>) => void} [onClick] - Click handler for the input container
 * @property {string} [leadingIconName] - Name of the leading icon
 * @property {string} [leadingIconViewBox] - ViewBox for the leading icon SVG
 * @property {string} [leadingIconFill] - Fill color for the leading icon
 * @property {string} [trailingIconName] - Name of the trailing icon
 * @property {string} [trailingIconClassName] - CSS class name for the trailing icon
 * @property {string} [trailingIconViewBox] - ViewBox for the trailing icon SVG
 * @property {string} [trailingIconFill] - Fill color for the trailing icon
 * @property {boolean} [readOnly] - Whether the input is read-only
 * @property {React.AriaRole} [role] - ARIA role for accessibility
 * @property {string} [ariaLabel] - ARIA label for accessibility
 * @property {boolean} [ariaExpanded] - ARIA expanded state for accessibility
 * @property {React.HTMLInputTypeAttribute} [type] - HTML input type attribute
 * @property {React.ChangeEventHandler<HTMLInputElement>} [onChange] - Change handler for input value changes
 * @property {Partial<MultiSelectInputComponentProps>} [multiSelectProps] - Props for multi-select input functionality
 * @property {string} [maxWidth] - Maximum width of the input container
 * @property {React.ReactNode} [rightElement] - Element to display on the right side of input
 * @property {boolean} [isDisabled] - Whether the input is disabled
 * @property {string} [minHeight] - Minimum height of the input container
 * @property {string} [className] - CSS class name for the input container
 * @property {string} [fieldWrapperWidth] - Width of the input container
 * @property {boolean} [autoFocus] - Whether the input should be focused on mount
 * @property {string} [step] - Step value for numeric inputs
 */

const Input: React.FC<InputProps> = ({
  inputType = 'normal',
  label,
  value,
  placeHolder,
  onClick,
  leadingIconName,
  leadingIconFill = 'var(--text-primary)',
  trailingIconClassName,
  trailingIconName,
  leadingIconViewBox = '0 0 18 18',
  trailingIconViewBox = '0 0 18 18',
  trailingIconFill = 'var(--text-primary)',
  ariaExpanded,
  ariaLabel,
  readOnly,
  role,
  type,
  onChange,
  multiSelectProps,
  maxWidth,
  rightElement,
  isDisabled,
  minHeight,
  step,
  className,
  fieldWrapperWidth,
  autoFocus,
  min,
  onBlur,
  onKeyDown,
  maxLength,
  labelIconColor,
  inputSize = 'large'
}) => {
  // ref variables
  const inputRef = React.useRef<HTMLInputElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  // state variable
  const [isActive, setIsActive] = React.useState(false);

  // Handle clicks outside the component
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        if (!multiSelectProps?.isDropdownOpen) {
          setIsActive(false);
        }
      }
    };
    // Set active when dropdown opens
    if (multiSelectProps?.isDropdownOpen && !isDisabled) {
      setIsActive(true);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [multiSelectProps?.isDropdownOpen, isDisabled, onBlur]);

  // handle input wrapper click
  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsActive(true);
    onClick && onClick(e);
  };

  // Update active state based on dropdown open state
  React.useEffect(() => {
    if (multiSelectProps?.isDropdownOpen && !isDisabled) {
      setIsActive(true);
    }
  }, [multiSelectProps?.isDropdownOpen, isDisabled]);

  // render input field
  const renderInputField = React.useMemo(() => {
    const handleInputBlur = () => {
      if (onBlur) {
        onBlur();
      }
      if (!multiSelectProps?.isDropdownOpen) {
        setIsActive(false);
      }
    };
    switch (inputType) {
      case 'normal':
        return (
          <InputField
            ref={inputRef}
            aria-label={ariaLabel}
            aria-expanded={ariaExpanded}
            role={role}
            min={min}
            readOnly={readOnly}
            disabled={isDisabled}
            placeholder={placeHolder}
            value={value}
            type={type}
            step={step}
            onChange={onChange}
            onBlur={handleInputBlur}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
            maxLength={maxLength}
            inputSize={inputSize}
          />
        );

      case 'tags':
        return (
          <>
            <MultiSelectInputComponent
              {...multiSelectProps}
              value={multiSelectProps?.value ?? value}
              inputRef={multiSelectProps?.inputRef ?? inputRef}
              // Ensure both the caller-provided blur handler (e.g. addEmailTag)
              // and the internal active state updates run on blur.
              onBlur={() => {
                if (multiSelectProps?.onBlur) {
                  multiSelectProps.onBlur();
                  return;
                }
                handleInputBlur();
              }}
            />
          </>
        );

      default:
        return (
          <InputField
            ref={inputRef}
            aria-label={ariaLabel}
            aria-expanded={ariaExpanded}
            role={role}
            min={min}
            readOnly={readOnly}
            disabled={isDisabled}
            placeholder={placeHolder}
            value={value}
            type={type}
            step={step}
            onChange={onChange}
            onBlur={handleInputBlur}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
            maxLength={maxLength}
            inputSize={inputSize}
          />
        );
    }
  }, [
    inputType,
    onBlur,
    multiSelectProps,
    ariaLabel,
    ariaExpanded,
    role,
    min,
    readOnly,
    isDisabled,
    placeHolder,
    value,
    type,
    step,
    onChange,
    onKeyDown,
    autoFocus,
    maxLength,
    inputSize
  ]);

  return (
    <>
      <FieldWrapper fieldWrapperWidth={fieldWrapperWidth} className={className}>
        {label && <Label isDisabled={isDisabled}>{label}</Label>}
        <InputWrapper
          role='button'
          ref={wrapperRef}
          maxWidth={maxWidth}
          minHeight={minHeight}
          inputType={inputType}
          onClick={handleWrapperClick}
          isActive={!readOnly && isActive}
          isDisabled={isDisabled}
          readOnly={readOnly}
          inputSize={inputSize}>
          {/* leading icon */}
          {labelIconColor ? (
            <LabelIcon labelIconColor={labelIconColor} />
          ) : (
            leadingIconName && (
              <Icon isDisabled={isDisabled}>
                <SVGIcon
                  name={leadingIconName}
                  width={ICON_SIZE.LG}
                  height={ICON_SIZE.LG}
                  viewBox={leadingIconViewBox}
                  fill={isDisabled ? 'var(--text-muted)' : leadingIconFill}
                />
              </Icon>
            )
          )}
          {renderInputField}
          {/* right element */}
          {rightElement && rightElement}
          {/* trailing icon */}
          {trailingIconName && (
            <Icon className={trailingIconClassName} isDisabled={isDisabled}>
              <SVGIcon
                name={trailingIconName}
                width={ICON_SIZE.LG}
                height={ICON_SIZE.LG}
                viewBox={trailingIconViewBox}
                fill={isDisabled ? 'var(--text-muted)' : trailingIconFill}
              />
            </Icon>
          )}
        </InputWrapper>
      </FieldWrapper>
    </>
  );
};

export default Input;
